import React from "react";

import { DeliveryServiceArea, Dispensary } from "custom-types/Dispensary";
import { DeliveryServiceRanges } from "custom-types/Store";
import { useSplitTreatment } from "hooks/useSplit";
import { getPaymentMethodsString } from "utils/fulfillmentUtils";

import ScheduleStatus from "components/botanic/ScheduleStatus";
import Scooter from "components/Icons/scooter.svg";

type Props = {
  dispensary: Dispensary;
  deliveryZone?: DeliveryServiceArea;
  fulfillmentType: "pickup" | "delivery" | "store";
  pickupReadinessLabel?: string;
};

const RetailerDetailsCard: React.FC<Props> = ({
  dispensary,
  deliveryZone,
  fulfillmentType,
  pickupReadinessLabel,
}) => {
  const {
    acceptsCash,
    acceptsCreditCards,
    acceptsDebitCards,
    deliveryPaymentMethods,
    deliveryServiceAreaRanges,
    pickupTimeEstimate,
    pickupMinutesMaximum,
    pickupMinutesMinimum,
  } = dispensary;
  const {
    minFee,
    minOrderMinimum,
    minDeliveryDurationMinimum,
    maxDeliveryDurationMaximum,
  } = deliveryServiceAreaRanges as DeliveryServiceRanges;

  const isDelivery = fulfillmentType?.toLowerCase() === "delivery";

  const scheduledDeliveryOn =
    useSplitTreatment("webWeb_scheduledDelivery_frontend") === "on";

  const paymentMethods = getPaymentMethodsString(
    {
      cash: acceptsCash,
      credit: acceptsCreditCards,
      debit: acceptsDebitCards,
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
    isDelivery ? (deliveryPaymentMethods as any) : []
  );

  // @ts-ignore (fix me please, do not replicate)
  const formattedTimeValue = (minutes) => {
    if (!Number.isInteger(minutes)) {
      return "Same day";
    }

    if (minutes === 2880) {
      return "Next day";
    }
    if (minutes === 1440) {
      return "Same day";
    }
    if (minutes > 60) {
      return `${Math.floor(minutes / 60)} hr ${minutes % 60} min`;
    }
    return `${minutes} min`;
  };

  // Since the min or max can be number, string, null or undefined we have
  // to check several things. First we check if its null OR undefined using
  // !=. Then we check if its an empty string. These first two checks prevent
  // the Number function from turning undefined, null or "" into a 0. Then
  // in the if statement we make sure the values are not false because
  // simply checking if they exist will return undefined if the values are 0.
  // Then we verify that the Number conversion did not create NaN with isNaN.
  const formattedTimeEstimate = (
    min?: number | string | null,
    max?: number | string | null
  ) => {
    const minimum = min != null && min !== "" && Number(min);
    const maximum = max != null && max !== "" && Number(max);

    if (
      minimum === false ||
      isNaN(minimum) ||
      maximum === false ||
      isNaN(maximum)
    ) {
      return undefined;
    }
    if (
      (minimum === 0 && maximum === 1440) ||
      (minimum === 0 && maximum === 0) ||
      minimum >= maximum
    ) {
      return "Same day";
    }
    if (minimum === 0 && maximum === 2880) {
      return "Next day";
    }
    if (minimum === 0 && maximum < 1440) {
      return `Up to ${formattedTimeValue(maximum)}`;
    }
    if (minimum === maximum) {
      return formattedTimeValue(minimum);
    }

    return `${formattedTimeValue(minimum)} - ${formattedTimeValue(maximum)}`;
  };

  const deliveryEstimate =
    formattedTimeEstimate(
      deliveryZone?.deliveryDurationMinimum,
      deliveryZone?.deliveryDurationMaximum
    ) ||
    formattedTimeEstimate(
      minDeliveryDurationMinimum,
      maxDeliveryDurationMaximum
    );

  const pickupEstimate = pickupReadinessLabel
    ? pickupReadinessLabel
    : formattedTimeEstimate(pickupMinutesMinimum, pickupMinutesMaximum) ||
      formattedTimeValue(pickupTimeEstimate);

  const timeEstimate = isDelivery ? deliveryEstimate : pickupEstimate;

  const fee =
    !isDelivery ||
    (deliveryZone?.fee || minFee) === 0 ||
    (!deliveryZone?.fee && !minFee)
      ? "Free!"
      : `$${deliveryZone?.fee || minFee}`;

  return (
    <div>
      <h2 className="text-xs uppercase">{fulfillmentType} Info</h2>
      {scheduledDeliveryOn &&
        fulfillmentType === "delivery" &&
        dispensary.scheduledDeliveryEnabled && (
          <div className="pt-sm pb-lg">
            <div className="border border-light-grey">
              <span className="flex p-2.5">
                <Scooter height="24" width="24" />
                <div className="pl-sm text-xs">
                  Scheduling available in checkout
                </div>
              </span>
            </div>
          </div>
        )}
      <div className="text-xs infoGrid mt-xs">
        <span className="font-bold">Today’s hours</span>

        <ScheduleStatus
          currentStatus={dispensary.currentStatuses?.[fulfillmentType]}
          scheduleType={fulfillmentType}
          timeZone={dispensary.timeZone}
        />
        {scheduledDeliveryOn && fulfillmentType === "delivery" ? (
          !dispensary.scheduledDeliveryEnabled && (
            <>
              <span data-testid="deliveryEstimate">
                {isDelivery ? "Delivery estimate" : "Time"}
              </span>
              <span>
                {!timeEstimate && "Ready in about"}
                &nbsp;
                {timeEstimate || "Same day"}
              </span>
            </>
          )
        ) : (
          <>
            <span>{isDelivery ? "Delivery estimate" : "Time"}</span>
            <span>
              {!timeEstimate && "Ready in about"}
              &nbsp;
              {timeEstimate || "Same day"}
            </span>
          </>
        )}
        {isDelivery && (
          <>
            <span>Minimum</span>
            <span>${deliveryZone?.orderMinimum || minOrderMinimum}</span>
          </>
        )}
        <span>Fee</span>
        <span>{fee}</span>
        <span>Payment</span>
        <span>{paymentMethods}</span>
      </div>

      <style jsx>{`
        .infoGrid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          grid-template-rows: repeat(5, 0.5fr);
          grid-row-gap: 4px;
        }
      `}</style>
    </div>
  );
};

export default RetailerDetailsCard;
